<script>
import APIMcp from '@app/services/API/Mcp';

export default {
  name: 'McpConnectionsList',
  data: () => ({
    loading: true,
    error: null,
    connections: [],
  }),
  computed: {
    isOwner() {
      return this.$store.getters['auth/isRoleOwner'];
    },
    currentUserUuid() {
      const user = this.$store.getters['auth/user'];
      return user ? user.uuid : null;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      this.error = null;
      try {
        const result = await APIMcp.getConnections();
        this.connections = Array.isArray(result) ? result : [];
      } catch (e) {
        // Un 404 signifie « rien à lister » (aucune connexion) : ce n'est pas une
        // erreur de chargement, on retombe simplement sur l'état vide. Les autres
        // échecs (réseau, 5xx…) affichent l'erreur et le bouton « Réessayer ».
        const status = e && e.response && e.response.status;
        this.connections = [];
        if (status !== 404) {
          this.error = 'Impossible de charger les connexions.';
        }
      } finally {
        this.loading = false;
      }
    },
    scopeLabel(scope) {
      const labels = {
        'mcp:read': 'Lecture',
        'mcp:stats': 'Statistiques',
        'mcp:write': 'Création et modification',
      };
      return labels[scope] || scope;
    },
    formatDate(value) {
      return value ? this.$moment(value).format('DD/MM/YYYY HH:mm') : '—';
    },
    confirmRevoke(connection) {
      this.$buefy.dialog.confirm({
        title: 'Révoquer cette connexion ?',
        message: 'Cette application perdra immédiatement l’accès à votre espace. Vous pourrez la reconnecter plus tard.',
        confirmText: 'Révoquer',
        cancelText: 'Annuler',
        type: 'is-danger',
        onConfirm: () => this.revoke(connection),
      });
    },
    async revoke(connection) {
      try {
        const targetUuid = connection.member_uuid !== this.currentUserUuid ? connection.member_uuid : null;
        await APIMcp.revokeConnection(connection.client_id, targetUuid);
        this.$buefy.toast.open({ message: 'Connexion révoquée.', type: 'is-success' });
        this.fetch();
      } catch (e) {
        this.$buefy.toast.open({ message: 'La révocation a échoué.', type: 'is-danger' });
      }
    },
  },
};
</script>

<template>
  <div>
    <b-loading :is-full-page="false" :active="loading" />

    <b-message v-if="!loading && error" type="is-danger">
      <p>{{ error }}</p>
      <b-button class="mt-3" size="is-small" @click="fetch">
        Réessayer
      </b-button>
    </b-message>

    <b-message v-else-if="!loading && connections.length === 0" type="is-info">
      Aucun assistant IA n'est connecté pour le moment.
    </b-message>

    <div v-else-if="!loading" class="table-container">
      <table class="table is-fullwidth">
        <thead>
          <tr>
            <th>Application</th>
            <th v-if="isOwner">
              Membre
            </th>
            <th>Permissions</th>
            <th>Dernière activité</th>
            <th class="has-text-centered">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="connection in connections" :key="connection.client_id + connection.member_uuid">
            <td class="has-text-weight-medium">
              {{ connection.client_name }}
            </td>
            <td v-if="isOwner">
              {{ connection.member_name || '—' }}
            </td>
            <td>
              <b-tag
                v-for="scope in connection.scopes"
                :key="scope"
                :type="scope === 'mcp:write' ? 'is-warning' : 'is-light'"
                class="mr-1"
              >
                {{ scopeLabel(scope) }}
              </b-tag>
            </td>
            <td>{{ formatDate(connection.last_used_at) }}</td>
            <td class="has-text-centered">
              <b-button size="is-small" type="is-danger" outlined @click="confirmRevoke(connection)">
                Révoquer
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
